import React, { useState } from 'react';
import { IconArrowRight, IconAt, IconKey, IconLayersIntersect, IconLogin2 } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { FormField } from '../../../shared/ui/FormField';
import { TextInput } from '../../../shared/ui/TextInput';
import { usePartnerAuth } from '../../../features/partner-auth';
import { PartnerAnalyticsSection } from './PartnerAnalyticsSection';
import { getManagerColor, getManagerInitials } from '../../../shared/lib/utils';

interface LoginFormValues {
  email: string;
  password: string;
}

const PartnerPage: React.FC = () => {
  const { currentPartner, isAuthenticated, isReady, login, logout } = usePartnerAuth();
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '' },
  });

  if (!isReady) return null;

  if (!isAuthenticated) {
    const handleFormSubmit = handleSubmit(async values => {
      setLoginError('');
      setLoginLoading(true);
      try {
        await login(values.email.trim(), values.password);
      } catch {
        setLoginError('Неверный логин или пароль');
      } finally {
        setLoginLoading(false);
      }
    });

    return (
      <div id="login-screen">
        <div className="login-card">
          <div className="logo">
            <div className="logo-icon">
              <IconLayersIntersect size={26} stroke={2} />
            </div>
            <div className="logo-text">CRM<span>Panel</span></div>
          </div>
          <div className="login-title">Партнёрский кабинет</div>
          <div className="login-sub">Войдите в свою учётную запись</div>
          <form onSubmit={handleFormSubmit}>
            <FormField className="field" htmlFor="partner-email" label="Логин" error={errors.email?.message}>
              <TextInput
                id="partner-email"
                controlClassName="field-with-icon"
                icon={<IconAt size={16} stroke={1.9} />}
                placeholder="e-mail"
                type="text"
                autoComplete="username"
                {...register('email', { required: 'Введите логин' })}
              />
            </FormField>
            <FormField className="field" htmlFor="partner-password" label="Пароль" error={errors.password?.message}>
              <TextInput
                id="partner-password"
                controlClassName="field-with-icon"
                icon={<IconKey size={16} stroke={1.9} />}
                placeholder="••••••••"
                type="password"
                autoComplete="current-password"
                {...register('password', { required: 'Введите пароль' })}
              />
            </FormField>
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="btn-login" disabled={loginLoading}>
              {loginLoading ? 'Вход...' : (
                <>
                  <span>Войти в систему</span>
                  <IconArrowRight size={18} stroke={2.2} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const initials = getManagerInitials(currentPartner!.name || currentPartner!.email, currentPartner!.email);
  const avatarColor = getManagerColor(currentPartner!.id);

  return (
    <div id="app-screen">
      <div className="topbar">
        <div className="topbar-logo">
          <div className="ti">
            <IconLayersIntersect size={17} stroke={2.2} />
          </div>
          CRM<span>Panel</span>
        </div>
        <div className="tsp" />
        <div className="tsep" />
        <div className="user-badge">
          <div className="user-av" style={{ background: avatarColor }}>{initials}</div>
          <div>
            <div className="user-name">{currentPartner!.name || currentPartner!.email}</div>
            <div className="user-role">Партнёр</div>
          </div>
        </div>
        <button className="btn-out" onClick={logout} type="button">
          <IconLogin2 size={13} stroke={2.5} />
          Выйти
        </button>
      </div>
      <div className="app-body">
        <main className="app-content adm-content">
          <PartnerAnalyticsSection />
        </main>
      </div>
    </div>
  );
};

export { PartnerPage };
